import React, { useState, useEffect, useMemo } from "react";
import { usePageHeader } from "../contexts/PageHeaderContext";
import MyTasksWidget from "../components/dashboard/MyTasksWidget";
import ProfitabilityWidget from "../components/dashboard/ProfitabilityWidget";
import { WelcomeBanner } from "../components/dashboard/WelcomeBanner";
import {
  DollarSign,
  Briefcase,
  TrendingUp,
  Phone,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  CircleDollarSign,
  PhoneCall,
  AlertCircle,
  AlertTriangle,
  Package,
  Star,
  MapPin,
  Clock,
  Loader2,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  revenueTrend,
  jobMix,
  statusColors,
} from "../data/mock";
import { dashboardApi, jobsApi, techniciansApi, pricebookApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import OnboardingChecklist from "../components/OnboardingChecklist";
import { isLowStock, effectiveLowStockThreshold, AdjustStockDialog } from "./Pricebook";

const activityIconMap = {
  check: CheckCircle2,
  finished: CheckCircle2,
  dispatched: PhoneCall,
  arrived: MapPin,
  started: TrendingUp,
  invoiced: CircleDollarSign,
  note: AlertCircle,
  photo: Star,
  created: Briefcase,
  scheduled: Clock,
  enroute: MapPin,
  status: AlertCircle,
};

const activityColorMap = {
  finished: "#10B981",
  dispatched: "#F59E0B",
  arrived: "#10B981",
  started: "#2563EB",
  invoiced: "#2563EB",
  note: "#0EA5E9",
  photo: "#EC4899",
  created: "#64748B",
  scheduled: "#2563EB",
  enroute: "#F97316",
  status: "#475569",
};

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [pricebook, setPricebook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustItem, setAdjustItem] = useState(null);

  const canSeeLowStock = user?.role === "admin" || user?.role === "dispatcher";
  const canReceive = user?.role === "admin";

  useEffect(() => {
    let cancelled = false;
    const loadAll = () => {
      const calls = [dashboardApi.stats(), dashboardApi.activity(6), jobsApi.list(), techniciansApi.list()];
      if (canSeeLowStock) calls.push(pricebookApi.list());
      return Promise.all(calls)
        .then((results) => {
          if (cancelled) return;
          const [s, a, j, t] = results;
          setStats(s);
          setActivity(a);
          setJobs(j);
          setTechnicians(t);
          if (canSeeLowStock && results.length > 4) setPricebook(results[4] || []);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
    };
    loadAll();
    const onJobCreated = () => loadAll();
    window.addEventListener("job-created", onJobCreated);
    window.addEventListener("job-status-changed", onJobCreated);
    return () => {
      cancelled = true;
      window.removeEventListener("job-created", onJobCreated);
      window.removeEventListener("job-status-changed", onJobCreated);
    };
  }, [canSeeLowStock]);

  // Low-stock list — materials at or below their threshold, most-depleted first
  const lowStockItems = useMemo(() => {
    return pricebook
      .filter((i) => isLowStock(i))
      .sort((a, b) => (a.stockOnHand || 0) - (b.stockOnHand || 0));
  }, [pricebook]);

  // Today's Jobs card: technicians see only their own not-completed jobs
  // scheduled today; admins/dispatchers see every not-completed job today,
  // regardless of assigned technician. Completed jobs never show here —
  // they remain visible on the Job History page only.
  const myTechId = useMemo(() => {
    if (user?.role !== "technician") return null;
    const email = (user?.email || "").toLowerCase();
    return technicians.find((t) => (t.email || "").toLowerCase() === email)?.id || null;
  }, [user, technicians]);

  const todaysJobs = useMemo(() => {
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    return jobs
      .filter((j) => (j.start || "").slice(0, 10) === ts && j.status !== "Completed")
      .filter((j) => (user?.role === "technician" ? j.techId === myTechId : true))
      .sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  }, [jobs, user, myTechId]);

  const kpiCards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: "Revenue Today", value: `$${stats.revenueToday.toLocaleString()}`, change: "+12.4%", up: true, icon: DollarSign, accent: "#2563EB" },
      { label: "Jobs Completed", value: stats.jobsCompleted, sub: `of ${stats.jobsScheduled} scheduled`, change: "+3", up: true, icon: Briefcase, accent: "#10B981" },
      { label: "Avg. Ticket", value: `$${stats.avgTicket.toLocaleString()}`, change: "+5.8%", up: true, icon: TrendingUp, accent: "#F59E0B" },
      { label: "Active Techs", value: `${stats.activeTechs} / ${stats.technicianCount}`, sub: `${stats.jobsUnassigned} jobs unassigned`, change: "live", up: true, icon: Phone, accent: "#8B5CF6" },
    ];
  }, [stats]);

  usePageHeader({ title: "Dashboard", subtitle: `${new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })} — Phoenix Branch` });

  return (
    <>
      <div className="p-6 space-y-6">
        {loading && (
          <div className="flex items-center gap-2 text-[12.5px] text-slate-500" data-testid="dashboard-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard from API…
          </div>
        )}
        {/* Welcome banner */}
        <WelcomeBanner />
        {/* Onboarding checklist (new company admins) */}
        <OnboardingChecklist />
        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {kpiCards.map((k) => {
            const Icon = k.icon;
            return (
              <div
                key={k.label}
                className="bg-white rounded-xl border border-slate-200/80 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wide">{k.label}</p>
                    <p className="mt-2 text-[26px] font-bold text-slate-900 tracking-tight leading-none">{k.value}</p>
                    {k.sub && <p className="mt-1 text-[11.5px] text-slate-500">{k.sub}</p>}
                  </div>
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${k.accent}15` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: k.accent }} strokeWidth={2.2} />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1 text-[12px] font-medium">
                  {k.up ? (
                    <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <ArrowDownRight className="h-3.5 w-3.5 text-red-500" />
                  )}
                  <span className={k.up ? "text-emerald-600" : "text-red-500"}>{k.change}</span>
                  <span className="text-slate-400">vs. last week</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Low-stock alert (admin + dispatcher) */}
        {canSeeLowStock && lowStockItems.length > 0 && (
          <div
            className="bg-white rounded-xl border border-rose-200 overflow-hidden shadow-sm"
            data-testid="dashboard-low-stock-alert"
          >
            <div className="px-5 py-3 border-b border-rose-100 bg-rose-50/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-rose-100 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-rose-600" />
                </div>
                <div>
                  <h3 className="text-[14px] font-semibold text-slate-900">
                    Low stock — {lowStockItems.length} item{lowStockItems.length === 1 ? "" : "s"} need attention
                  </h3>
                  <p className="text-[11.5px] text-slate-500">
                    Materials at or below their reorder threshold. {canReceive ? "Click Receive to log incoming stock." : "Admin can receive stock."}
                  </p>
                </div>
              </div>
              <a
                href="/pricebook"
                className="text-[11.5px] font-semibold text-blue-700 hover:text-blue-900"
              >
                Open Pricebook →
              </a>
            </div>
            <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {lowStockItems.map((m) => {
                const threshold = effectiveLowStockThreshold(m);
                const percent = threshold > 0 ? Math.min(100, ((m.stockOnHand || 0) / threshold) * 100) : 0;
                return (
                  <div
                    key={m.id}
                    data-testid={`low-stock-row-${m.id}`}
                    className="px-5 py-2.5 flex items-center gap-3"
                  >
                    <div className="h-8 w-8 rounded-md bg-violet-50 flex items-center justify-center shrink-0">
                      <Package className="h-4 w-4 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">
                        {m.name}
                        <span className="ml-1.5 font-mono text-[10.5px] text-slate-400">{m.id}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 max-w-[200px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              (m.stockOnHand || 0) === 0
                                ? "bg-rose-600"
                                : (m.stockOnHand || 0) < threshold / 2
                                  ? "bg-rose-500"
                                  : "bg-amber-500"
                            }`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <div className="text-[11px] text-slate-500">
                          <span className={`font-bold ${(m.stockOnHand || 0) === 0 ? "text-rose-600" : "text-slate-700"}`}>
                            {Number(m.stockOnHand).toFixed(0)}
                          </span>
                          <span className="text-slate-400"> / threshold {threshold}{m.unit ? ` ${m.unit}` : ""}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setAdjustItem(m)}
                      disabled={!canReceive}
                      data-testid={`low-stock-receive-${m.id}`}
                      className="h-8 px-2.5 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11.5px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
                      title={canReceive ? "Receive / adjust stock" : "Admin only"}
                    >
                      <Package className="h-3 w-3" /> Receive
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* My tasks — due today & overdue */}
        <MyTasksWidget />

        {/* Profitability widget — admin only */}
        {user?.role === "admin" && <ProfitabilityWidget />}

        {/* Chart row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200/80 p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">Revenue — Last 7 Days</h3>
                <p className="text-[12px] text-slate-500">Daily booked revenue across all branches</p>
              </div>
              <div className="flex items-center gap-2">
                {["7D", "30D", "90D"].map((r, i) => (
                  <button
                    key={r}
                    className={`h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors ${
                      i === 0
                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                        : "text-slate-500 hover:bg-slate-100 border border-transparent"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                    formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.5} fill="url(#revGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/80 p-5">
            <h3 className="text-[15px] font-semibold text-slate-900">Job Mix</h3>
            <p className="text-[12px] text-slate-500">By trade, this week</p>
            <div className="h-44 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={jobMix} dataKey="value" innerRadius={48} outerRadius={70} paddingAngle={2} stroke="none">
                    {jobMix.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-1">
              {jobMix.map((j) => (
                <div key={j.name} className="flex items-center justify-between text-[12.5px]">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: j.color }} />
                    <span className="text-slate-700">{j.name}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{j.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Today's jobs + activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200/80">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">Today&apos;s Jobs</h3>
                <p className="text-[12px] text-slate-500">{todaysJobs.length} jobs scheduled — live status</p>
              </div>
              <button className="text-[12px] font-medium text-blue-600 hover:text-blue-700">View all →</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                    <th className="text-left font-semibold px-5 py-2.5">Job</th>
                    <th className="text-left font-semibold py-2.5">Customer</th>
                    <th className="text-left font-semibold py-2.5">Time</th>
                    <th className="text-left font-semibold py-2.5">Tech</th>
                    <th className="text-left font-semibold py-2.5">Status</th>
                    <th className="text-right font-semibold px-5 py-2.5">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {todaysJobs.slice(0, 6).map((j) => {
                    const sc = statusColors[j.status] || { bg: "#E5E7EB", text: "#374151" };
                    const start = j.start ? new Date(j.start) : null;
                    const time = start ? start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";
                    return (
                      <tr key={j.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3">
                          <div className="font-semibold text-slate-900">{j.id}</div>
                          <div className="text-[11.5px] text-slate-500">{j.type}</div>
                        </td>
                        <td className="py-3">
                          <div className="text-slate-800">{j.customer}</div>
                          <div className="text-[11.5px] text-slate-500 flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {j.address.split(",")[0]}
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1 text-slate-700">
                            <Clock className="h-3.5 w-3.5 text-slate-400" /> {time}
                          </div>
                        </td>
                        <td className="py-3 text-slate-700">{j.tech}</td>
                        <td className="py-3">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                            style={{ background: sc.bg, color: sc.text }}
                          >
                            {j.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-900">
                          ${(j.value || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/80">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-[15px] font-semibold text-slate-900">Recent Activity</h3>
              <p className="text-[12px] text-slate-500">Real-time updates from the field</p>
            </div>
            <ul className="px-5 py-3 space-y-3" data-testid="dashboard-activity-feed">
              {activity.map((a, i) => {
                const Icon = activityIconMap[a.type] || CheckCircle2;
                const color = activityColorMap[a.type] || "#64748B";
                return (
                  <li key={`${a.jobId}-${i}`} className="flex gap-3">
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `${color}18` }}
                    >
                      <Icon className="h-4 w-4" style={{ color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] text-slate-800 leading-snug">
                        <span className="font-semibold">{a.jobId}</span> · {a.label}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{relTime(a.time)} {a.by ? `· ${a.by}` : ""}</p>
                    </div>
                  </li>
                );
              })}
              {activity.length === 0 && !loading && (
                <li className="text-[12.5px] text-slate-400 py-4 text-center">No recent activity</li>
              )}
            </ul>
          </div>
        </div>

        {/* Technician status strip */}
        <div className="bg-white rounded-xl border border-slate-200/80">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-[15px] font-semibold text-slate-900">Technician Status</h3>
              <p className="text-[12px] text-slate-500">{technicians.length} technicians on shift</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 p-5">
            {technicians.map((t) => {
              const sc = statusColors[t.status] || { bg: "#E5E7EB", text: "#374151" };
              return (
                <div key={t.id} className="border border-slate-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="h-9 w-9 rounded-full text-white text-[12px] font-semibold flex items-center justify-center shrink-0"
                      style={{ background: t.color }}
                    >
                      {t.avatar}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{t.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{t.role}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold"
                      style={{ background: sc.bg, color: sc.text }}
                    >
                      {t.status}
                    </span>
                    <span className="text-[11px] text-slate-500">{t.jobsToday} jobs</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {adjustItem && (
        <AdjustStockDialog
          item={adjustItem}
          onClose={() => setAdjustItem(null)}
          onAdjusted={(updated) => {
            setPricebook((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
            setAdjustItem(null);
          }}
        />
      )}
    </>
  );
}
